import { Component, DestroyRef, ElementRef, ViewChild, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { forkJoin, of } from 'rxjs';
import { catchError, take } from 'rxjs/operators';
import { SearchInputComponent } from '../../../../../components/inputs/search-input/search-input.component';
import {
  SortDropdownComponent,
  SortDropdownGroup,
  buildSortGroups,
} from '../../../../../components/inputs/sort-dropdown/sort-dropdown.component';
import { TagsTableComponent } from '../../../../../components/tables/tags-table/tags-table.component';

// Types and Modules
import { ITag } from '../../../../../types/contactModule';

// services
import { ContactsService } from '../../../../../services/contacts/contacts.service';
import { GroupAudienceService } from '../../../../../services/segments/group-audience.service';
import { IOrganizationSort } from '../../organization-list/organization-list.component';
import { MessageService } from '../../../../../services/message/message.service';
import { PromptCardComponent } from '../../../../../components/toasts-and-cards/prompt-card/prompt-card.component';
import { HasPermissionDirective } from '../../../../../directives/has-permission.directive';
import { countNoun, sumCounts } from '../../../../../components/tables/group-table/group-rows';

@Component({
  selector: 'tag-list',
  standalone: true,
  imports: [
    SearchInputComponent,
    SortDropdownComponent,
    TagsTableComponent,
    PromptCardComponent,
    HasPermissionDirective,
  ],
  templateUrl: './tag-list.component.html',
  styleUrls: ['../../../../../components/tables/group-table/group-list.css', './tag-list.component.css'],
})
export class TagListComponent {
  router = inject(Router);
  contactsService = inject(ContactsService);
  messageService = inject(MessageService);
  private audience = inject(GroupAudienceService);
  private destroyRef = inject(DestroyRef);
  private host: ElementRef<HTMLElement> = inject(ElementRef);

  @ViewChild(TagsTableComponent) tableChild?: TagsTableComponent;
  @ViewChild(SearchInputComponent) searchInput?: SearchInputComponent;

  searchTerm: string = '';
  sortCriteria: IOrganizationSort = 'name,asc';
  total: number | null = null;

  readonly sortGroups: SortDropdownGroup[] = buildSortGroups({
    nameField: 'name',
    createdField: 'createdAt',
    modifiedField: 'modifiedAt',
  });

  selectedTags: ITag[] = [];

  /** Search and sort for the table. A new object on each change; page and size live in the table. */
  params: Record<string, any> = { sort: 'name,asc' };

  // delete selected tags
  showDeleteSelectedTagsPrompt: boolean = false;
  deleting = false;
  selectedMembers: { total: number; complete: boolean } | null = null;

  get countLine(): string {
    if (this.total === null) return '';
    const line = countNoun(this.total, 'tag', 'tags');
    return this.searchTerm.trim() ? `${line} ${this.total === 1 ? 'matches' : 'match'}` : line;
  }

  get selectionLabel(): string {
    return `${countNoun(this.selectedTags.length, 'tag', 'tags')} selected`;
  }

  get deleteTitle(): string {
    const count = this.selectedTags.length;
    return count === 1 ? `Delete ${this.selectedTags[0]?.name || 'this tag'}?` : `Delete ${count} tags?`;
  }

  get deleteButton(): string {
    const count = this.selectedTags.length;
    return count === 1 ? 'Delete tag' : `Delete ${count} tags`;
  }

  get deleteImpact(): string {
    const members = this.selectedMembers;
    if (!members) return 'Counting the contacts with these tags…';
    const single = this.selectedTags.length === 1;
    if (members.total === 0 && members.complete) return single ? 'No contacts have this tag.' : 'No contacts have these tags.';
    if (!members.complete) return 'The tags come off their contacts. The contacts stay in your contact list.';
    if (single) {
      return `${countNoun(members.total, 'contact loses', 'contacts lose')} this tag. They stay in your contact list.`;
    }
    // A contact can carry several of these tags, so the sum is an upper bound.
    return `The tags come off up to ${countNoun(members.total, 'contact', 'contacts')}. The contacts stay in your contact list.`;
  }

  handleAction(action: string) {
    if (this.selectedTags.length === 0) return;
    switch (action) {
      case 'send-sms':
        this.sendSMStoSelectedTags();
        break;
      case 'delete':
        this.openDeleteSelectedTagsPrompt();
        break;
    }
  }

  updateSelectedTags(tags: ITag[]) {
    this.selectedTags = [...tags];
  }

  resetSelectedTags() {
    this.selectedTags = [];
    this.tableChild?.deSelectAllTags();
    this.keepFocusOnPage();
  }

  /**
   * The selection bar goes when nothing is ticked. If it held focus, move
   * focus to the search box rather than letting it drop to the page top.
   */
  private keepFocusOnPage() {
    setTimeout(() => {
      const active = this.host.nativeElement.ownerDocument?.activeElement;
      if (active && active !== active.ownerDocument.body) return;
      this.host.nativeElement.querySelector<HTMLInputElement>('.gl-search input')?.focus();
    });
  }

  closeDeleteSelectedTagsPrompt() {
    if (this.deleting) return;
    this.showDeleteSelectedTagsPrompt = false;
  }

  openDeleteSelectedTagsPrompt() {
    this.showDeleteSelectedTagsPrompt = true;
    this.selectedMembers = null;
    const counts = this.selectedTags
      .filter((tag) => !!tag.uuid)
      .map((tag) =>
        this.audience.memberCount({ kind: 'tag', uuid: tag.uuid! }).pipe(
          take(1),
          catchError(() => of(null))
        )
      );
    if (!counts.length) {
      this.selectedMembers = { total: 0, complete: false };
      return;
    }
    forkJoin(counts)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((values) => (this.selectedMembers = sumCounts(values)));
  }

  deleteSelectedTags() {
    const count = this.selectedTags.length;
    if (!count || this.deleting) return;
    this.deleting = true;
    this.contactsService.deleteSelectedTags(this.selectedTags.map((tag) => tag.uuid as string)).subscribe({
      next: () => {
        this.deleting = false;
        this.selectedTags.forEach((tag) => this.audience.invalidate({ kind: 'tag', uuid: tag.uuid as string }));
        this.resetSelectedTags();
        this.showDeleteSelectedTagsPrompt = false;
      },
      error: () => {
        this.deleting = false;
        this.showDeleteSelectedTagsPrompt = false;
        this.contactsService.showError(
          count === 1 ? 'The tag was not deleted. Try again.' : `The ${count} tags were not deleted. Try again.`
        );
      },
    });
  }

  updateSearchTerm(term: string) {
    this.searchTerm = term ?? '';
    this.refresh();
  }

  clearSearch() {
    this.searchInput?.writeValue('');
    this.searchTerm = '';
    this.refresh();
  }

  sendSMStoSelectedTags() {
    if (this.selectedTags.length) {
      this.messageService.updateSelectedTags(this.selectedTags);
      this.router.navigate(['/organization/messages/create']);
      this.resetSelectedTags();
    }
  }

  updateSortCriteria(criteria: IOrganizationSort) {
    this.sortCriteria = criteria;
    this.refresh();
  }

  private refresh() {
    const params: Record<string, any> = { sort: this.sortCriteria };
    const term = this.searchTerm.trim();
    if (term) params['name.contains'] = term;
    this.params = params;
  }

  updateTotal(total: number) {
    this.total = total;
  }

  get reactiveParams() {
    return this.params;
  }
}
